// demo-controls — wireframe skin module
// Token disposition: color tokens overridden to wf-* namespace; spacing/sizing pass-through
// Panel chrome: hardcoded dark glass palette is architectural invariant (MVR D5/Part 16) —
//   wireframe skin is registered to satisfy skin resolution contract but is never resolved
//   in practice (base class forces this.skin = 'default' on connectedCallback).
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: contents;
  }

  [part="panel"] {
    position: fixed;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    padding-block: 6px;
    padding-inline: 8px;
    background: var(--run-wf-surface);
    border: 1px dashed var(--run-wf-border);
    border-radius: 9999px;
    z-index: var(--run-demo-controls-z-index, 9999);
    font-family: var(--run-wf-font-family);
    font-weight: 400;
  }

  [part="panel"][data-position="bottom-right"] {
    inset-block-end: var(--run-demo-controls-offset, 16px);
    inset-inline-end: var(--run-demo-controls-offset, 16px);
  }

  [part="panel"][data-position="bottom-left"] {
    inset-block-end: var(--run-demo-controls-offset, 16px);
    inset-inline-start: var(--run-demo-controls-offset, 16px);
  }

  [part="panel"][data-position="top-right"] {
    inset-block-start: var(--run-demo-controls-offset, 16px);
    inset-inline-end: var(--run-demo-controls-offset, 16px);
  }

  [part="panel"][data-position="top-left"] {
    inset-block-start: var(--run-demo-controls-offset, 16px);
    inset-inline-start: var(--run-demo-controls-offset, 16px);
  }

  [part="panel"][hidden] {
    display: none;
  }

  [part="expand"] {
    position: fixed;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    background: var(--run-wf-surface);
    border: 1px dashed var(--run-wf-border);
    border-radius: 9999px;
    z-index: var(--run-demo-controls-z-index, 9999);
    font-family: var(--run-wf-font-family);
    font-weight: 400;
  }

  [part="expand"][data-position="bottom-right"] {
    inset-block-end: var(--run-demo-controls-offset, 16px);
    inset-inline-end: var(--run-demo-controls-offset, 16px);
  }

  [part="expand"][data-position="bottom-left"] {
    inset-block-end: var(--run-demo-controls-offset, 16px);
    inset-inline-start: var(--run-demo-controls-offset, 16px);
  }

  [part="expand"][data-position="top-right"] {
    inset-block-start: var(--run-demo-controls-offset, 16px);
    inset-inline-end: var(--run-demo-controls-offset, 16px);
  }

  [part="expand"][data-position="top-left"] {
    inset-block-start: var(--run-demo-controls-offset, 16px);
    inset-inline-start: var(--run-demo-controls-offset, 16px);
  }

  [part="expand"][hidden] {
    display: none;
  }

  [part="announcer"] {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
`;

export function render(host) {
  const { collapsed, theme, locale, locales, pageSkin, position, _strings } = host;

  // Compute effective corner position with RTL inline-axis mirror (MVR D4)
  const dir = document.documentElement.dir;
  let effectivePosition = position;
  if (dir === 'rtl') {
    if (position === 'bottom-right')      effectivePosition = 'bottom-left';
    else if (position === 'bottom-left')  effectivePosition = 'bottom-right';
    else if (position === 'top-right')    effectivePosition = 'top-left';
    else if (position === 'top-left')     effectivePosition = 'top-right';
  }

  // Resolve localized strings for active locale; fall back to empty object
  const strings = (_strings && _strings[locale]) || {};

  // Compute next locale in cycle (MVR Part 4 §Locale Cycling)
  const localesArr = Array.isArray(locales) ? locales : [];
  const currentIdx = localesArr.findIndex(function(l) { return l.code === locale; });
  const nextLocale = localesArr.length > 0
    ? localesArr[(currentIdx + 1) % localesArr.length]
    : null;

  // Resolve state-dependent aria-labels (MVR Invariant 8)
  const toolbarLabel   = strings.toolbar_label  || 'Design system demo controls';
  const themeLabel     = theme === 'dark'
    ? (strings.theme_to_light   || 'Switch to light theme')
    : (strings.theme_to_dark    || 'Switch to dark theme');
  const skinLabel      = pageSkin === 'default'
    ? (strings.skin_to_wireframe || 'Switch to wireframe skin')
    : (strings.skin_to_default   || 'Switch to default skin');
  const collapseLabel  = strings.collapse || 'Collapse controls';
  const expandLabel    = strings.expand   || 'Expand controls';

  // Locale button: rendered only when locales array is non-empty (MVR §Locale Cycling)
  let localeButtonHtml = '';
  if (localesArr.length > 0 && nextLocale) {
    const rawTemplate = strings.locale_to_next || 'Switch locale to {label}';
    const localeLabel = rawTemplate.replace('{label}', nextLocale.label);
    localeButtonHtml = `<run-button variant="ghost" size="sm" data-action="locale" aria-label="${localeLabel}" tabindex="-1">${nextLocale.label}</run-button>`;
  }

  // Visibility: toolbar hidden when collapsed; expand button hidden when expanded
  const toolbarHiddenAttr = collapsed ? ' hidden' : '';
  const expandHiddenAttr  = collapsed ? '' : ' hidden';

  // Initial roving-tabindex values:
  //   expanded  → first toolbar button (theme) gets tabindex="0", rest "-1"
  //   collapsed → expand button gets tabindex="0", toolbar items irrelevant (hidden)
  const firstToolbarTabindex = collapsed ? '-1' : '0';

  host.shadowRoot.innerHTML = `<div
      part="panel"
      role="toolbar"
      aria-label="${toolbarLabel}"
      aria-orientation="horizontal"
      id="run-demo-controls-toolbar"
      data-position="${effectivePosition}"${toolbarHiddenAttr}
    ><run-button
        variant="ghost"
        size="sm"
        data-action="theme"
        aria-label="${themeLabel}"
        tabindex="${firstToolbarTabindex}"
      ></run-button>${localeButtonHtml}<run-button
        variant="ghost"
        size="sm"
        data-action="skin"
        aria-label="${skinLabel}"
        tabindex="-1"
      ></run-button><run-button
        variant="ghost"
        size="sm"
        data-action="collapse"
        aria-label="${collapseLabel}"
        aria-expanded="true"
        aria-controls="run-demo-controls-toolbar"
        tabindex="-1"
      ></run-button></div><run-button
    part="expand"
    variant="ghost"
    size="sm"
    data-action="expand"
    aria-label="${expandLabel}"
    aria-expanded="false"
    aria-controls="run-demo-controls-toolbar"
    data-position="${effectivePosition}"
    tabindex="${collapsed ? '0' : '-1'}"${expandHiddenAttr}
  ></run-button><span part="announcer" aria-live="polite" aria-atomic="true"></span>`;
}